/**
 * Export validation service
 * Provides reusable validation logic for invoice exports.
 */

/**
 * Check if an invoice has missing required fields.
 * @param {Object} invoice - Invoice object
 * @returns {boolean} True if any required field is missing
 */
export const hasMissingInfo = (invoice) => {
  return !invoice.invoice_number || invoice.invoice_number === '' ||
         // VAT numbers and VAT amount are no longer mandatory
         // !invoice.company_vat || invoice.company_vat === '' ||
         // !invoice.vendor_vat || invoice.vendor_vat === '' ||
         !invoice.vendor_name || invoice.vendor_name === '' ||
         !invoice.currency || invoice.currency === '' ||
         // !invoice.vat_amount || invoice.vat_amount === '' ||
         !invoice.total_amount || invoice.total_amount === '';
};

/**
 * Get missing information per invoice.
 * @param {Array} invoices - List of invoice objects
 * @param {Function} t - i18n translate function (optional)
 * @returns {Array} Array of objects with invoice identifier and missing fields
 */
export const getMissingInfoInvoices = (invoices, t = null) => {
  const result = [];
  
  // Helper function to get translated field name
  const getFieldName = (key) => {
    if (t) {
      return t(`common.fieldNames.${key}`);
    }
    // Fallback to English
    const fallbacks = {
      'invoiceNumber': 'Invoice Number',
      'companyVat': 'Company VAT/Tax ID',
      'partnerVat': 'Partner VAT/Tax ID',
      'partnerName': 'Partner Name',
      'currency': 'Currency',
      'vatAmount': 'VAT Amount',
      'totalAmount': 'Total Amount'
    };
    return fallbacks[key] || key;
  };
  
  invoices.forEach(invoice => {
    const missing = [];
    
    if (!invoice.invoice_number || invoice.invoice_number === '') {
      missing.push(getFieldName('invoiceNumber'));
    }
    // VAT numbers are no longer mandatory
    // if (!invoice.company_vat || invoice.company_vat === '') {
    //   missing.push(getFieldName('companyVat'));
    // }
    // if (!invoice.vendor_vat || invoice.vendor_vat === '') {
    //   missing.push(getFieldName('partnerVat'));
    // }
    if (!invoice.vendor_name || invoice.vendor_name === '') {
      missing.push(getFieldName('partnerName'));
    }
    if (!invoice.currency || invoice.currency === '') {
      missing.push(getFieldName('currency'));
    }
    // VAT amount is no longer mandatory
    // if (!invoice.vat_amount || invoice.vat_amount === '') {
    //   missing.push(getFieldName('vatAmount'));
    // }
    if (!invoice.total_amount || invoice.total_amount === '') {
      missing.push(getFieldName('totalAmount'));
    }
    
    if (missing.length > 0) {
      // Create a readable label for the invoice
      // Try invoice_number, then document_number, then date-based, then ID-based
      let invoiceLabel = invoice.invoice_number || invoice.document_number;
      
      // If no invoice number, use date-based or ID-based label
      if (!invoiceLabel) {
        if (invoice.invoice_date) {
          invoiceLabel = `Invoice from ${invoice.invoice_date}`;
        } else {
          invoiceLabel = `Invoice ${invoice.id.substring(0, 8)}...`;
        }
      }
      
      result.push({
        id: invoice.id,
        invoice: invoiceLabel,
        missing: missing
      });
    }
  });
  
  return result;
};

/**
 * Normalize a company name for comparison purposes.
 * Strips insignificant differences like extra dashes, spaces, and punctuation.
 * "ВИВ - 97 - ООД", "ВИВ - 97 ООД", "ВИВ-97 ООД" all normalize to the same string.
 */
const normalizeCompanyName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\s*-\s*/g, ' ')    // Replace dashes (with surrounding spaces) with single space
    .replace(/\s*–\s*/g, ' ')    // En-dash
    .replace(/\s*—\s*/g, ' ')    // Em-dash
    .replace(/["""''`]/g, '')     // Remove quotes
    .replace(/\.$/, '')           // Remove trailing dot
    .replace(/\s+/g, ' ')        // Collapse multiple spaces
    .trim();
};

/**
 * Detect duplicate VAT numbers with different names across invoices.
 * @param {Array} invoices - List of invoice objects
 * @returns {Set} Set of invoice IDs that have conflicts (same VAT, different name)
 */
export const detectVatConflicts = (invoices) => {
  const conflicts = new Set();
  const vatGroups = new Map(); // vat -> { names: Map(name -> invoiceIds with role info) }
  
  // Group invoices by VAT and name (case-insensitive) - check BOTH buyer and seller
  invoices.forEach(invoice => {
    // Check seller VAT/name
    if (invoice.seller_vat && invoice.seller_vat !== '') {
      const vat = invoice.seller_vat;
      const name = normalizeCompanyName(invoice.seller_name);
      
      if (!vatGroups.has(vat)) {
        vatGroups.set(vat, { names: new Map() });
      }
      
      const group = vatGroups.get(vat);
      if (!group.names.has(name)) {
        group.names.set(name, []);
      }
      group.names.get(name).push({ id: invoice.id, role: 'seller', originalName: invoice.seller_name });
    }
    
    // Check buyer VAT/name
    if (invoice.buyer_vat && invoice.buyer_vat !== '') {
      const vat = invoice.buyer_vat;
      const name = normalizeCompanyName(invoice.buyer_name);
      
      if (!vatGroups.has(vat)) {
        vatGroups.set(vat, { names: new Map() });
      }
      
      const group = vatGroups.get(vat);
      if (!group.names.has(name)) {
        group.names.set(name, []);
      }
      group.names.get(name).push({ id: invoice.id, role: 'buyer', originalName: invoice.buyer_name });
    }
  });
  
  // For each VAT with multiple names, flag only the minority name(s)
  vatGroups.forEach((group, vat) => {
    if (group.names.size >= 2) {
      // Multiple names for same VAT - find which has fewer invoices
      const nameCounts = Array.from(group.names.entries()).map(([name, entries]) => ({
        name,
        count: entries.length,
        entries: entries
      }));
      
      // Find minimum count (the minority/typo)
      const minCount = Math.min(...nameCounts.map(nc => nc.count));
      
      // Flag only invoices with the minority name(s)
      nameCounts.forEach(({ name, count, entries }) => {
        if (count === minCount) {
          entries.forEach(entry => conflicts.add(entry.id));
        }
      });
    }
  });
  
  // Debug logging
  console.log('VAT conflict detection:', {
    vatMap: Array.from(vatGroups.entries()).map(([vat, group]) => ({
      vat,
      names: Array.from(group.names.keys()),
      counts: Array.from(group.names.entries()).map(([name, entries]) => ({ name, count: entries.length }))
    })),
    conflicts: Array.from(conflicts)
  });
  
  return conflicts;
};

/**
 * Get detailed information about VAT conflicts for display
 * @param {Array} invoices - List of invoice objects
 * @returns {Array} Array of conflict details with invoice info
 */
export const getVatConflictDetails = (invoices) => {
  const conflicts = detectVatConflicts(invoices);
  const result = [];
  const vatGroups = new Map(); // vat -> { names: Map(name -> entries) }
  
  // Group invoices by VAT and name - check BOTH buyer and seller
  invoices.forEach(invoice => {
    // Check seller
    if (invoice.seller_vat && invoice.seller_vat !== '') {
      const vat = invoice.seller_vat;
      const name = normalizeCompanyName(invoice.seller_name);
      
      if (!vatGroups.has(vat)) {
        vatGroups.set(vat, { names: new Map() });
      }
      
      const group = vatGroups.get(vat);
      if (!group.names.has(name)) {
        group.names.set(name, []);
      }
      group.names.get(name).push({ ...invoice, role: 'seller', currentName: invoice.seller_name });
    }
    
    // Check buyer
    if (invoice.buyer_vat && invoice.buyer_vat !== '') {
      const vat = invoice.buyer_vat;
      const name = normalizeCompanyName(invoice.buyer_name);
      
      if (!vatGroups.has(vat)) {
        vatGroups.set(vat, { names: new Map() });
      }
      
      const group = vatGroups.get(vat);
      if (!group.names.has(name)) {
        group.names.set(name, []);
      }
      group.names.get(name).push({ ...invoice, role: 'buyer', currentName: invoice.buyer_name });
    }
  });
  
  // For each conflicted invoice, show what other names exist for the same VAT
  vatGroups.forEach((group, vat) => {
    if (group.names.size >= 2) {
      const allNames = Array.from(group.names.keys());
      
      group.names.forEach((entries, name) => {
        entries.forEach(entry => {
          if (conflicts.has(entry.id)) {
            // This invoice has a conflict
            const otherNames = allNames.filter(n => n !== name);
            
            // Create invoice label
            let invoiceLabel = entry.invoice_number || entry.document_number;
            if (!invoiceLabel) {
              if (entry.invoice_date) {
                invoiceLabel = `Invoice from ${entry.invoice_date}`;
              } else {
                invoiceLabel = `Invoice ${entry.id.substring(0, 8)}...`;
              }
            }
            
            // Get actual case-sensitive other names
            const otherNamesList = otherNames.map(n => {
              const otherEntry = group.names.get(n)[0];
              return otherEntry.currentName;
            });
            
            result.push({
              id: entry.id,
              invoice: invoiceLabel,
              vat: vat,
              role: entry.role,
              currentName: entry.currentName,
              otherNames: otherNamesList
            });
          }
        });
      });
    }
  });
  
  return result;
};

/**
 * Check if a specific invoice has a VAT conflict.
 * @param {Array} invoices - List of invoice objects
 * @param {number} invoiceId - Invoice ID to check
 * @returns {boolean} True if invoice has a VAT conflict
 */
export const hasVatConflict = (invoices, invoiceId) => {
  const conflicts = detectVatConflicts(invoices);
  return conflicts.has(invoiceId);
};

/**
 * Sort invoices by missing info (missing first).
 * @param {Array} invoices - List of invoice objects
 * @returns {Array} Sorted copy of invoices
 */
export const sortByMissingInfo = (invoices) => {
  return [...invoices].sort((a, b) => {
    const aMissing = hasMissingInfo(a);
    const bMissing = hasMissingInfo(b);
    
    if (aMissing && !bMissing) return -1;
    if (!aMissing && bMissing) return 1;
    return 0;
  });
};

/**
 * Detect unique partner names when there are 3 or more different names.
 * Returns a Set of invoice IDs that have unique (appearing only once) partner names.
 * @param {Array} invoices - List of invoice objects
 * @returns {Set} Set of invoice IDs with unique partner names
 */
// Helper function to calculate similarity between two strings (Levenshtein distance)
const calculateSimilarity = (str1, str2) => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  const distance = matrix[len1][len2];
  const maxLength = Math.max(len1, len2);
  return 1 - distance / maxLength;
};

export const detectUniquePartnerNames = (invoices) => {
  // Need at least 2 invoices to detect inconsistencies
  if (invoices.length < 2) {
    return new Set();
  }
  
  const nameCount = {};
  const nameToInvoiceIds = {};
  
  // Count occurrences of each partner name (case-insensitive)
  invoices.forEach(invoice => {
    const name = (invoice.vendor_name || '').trim().toLowerCase();
    if (name && name !== '') {
      nameCount[name] = (nameCount[name] || 0) + 1;
      if (!nameToInvoiceIds[name]) {
        nameToInvoiceIds[name] = [];
      }
      nameToInvoiceIds[name].push(invoice.id);
    }
  });
  
  const differentNamesCount = Object.keys(nameCount).length;
  
  // If there's only 1 unique name, don't flag it
  const uniqueNames = Object.keys(nameCount).filter(name => nameCount[name] === 1);
  if (uniqueNames.length === 1) {
    return new Set();
  }
  
  // Check for VAT/Tax ID inconsistencies (same VAT, different names)
  const vatGroups = {};
  
  invoices.forEach(invoice => {
    const vat = (invoice.vendor_vat || '').trim();
    const name = (invoice.vendor_name || '').trim().toLowerCase();
    
    if (vat && vat !== '' && name && name !== '') {
      if (!vatGroups[vat]) {
        vatGroups[vat] = {
          names: new Set(),
          invoiceIds: []
        };
      }
      vatGroups[vat].names.add(name);
      vatGroups[vat].invoiceIds.push(invoice.id);
    }
  });
  
  // Find VAT numbers that have 2+ different names (inconsistency)
  const inconsistentInvoiceIds = new Set();
  Object.entries(vatGroups).forEach(([vat, data]) => {
    if (data.names.size >= 2) {
      console.log(`🔍 VAT "${vat}" has ${data.names.size} different names:`, Array.from(data.names));
      
      // Same VAT but different names - find which name has fewer invoices
      const namesArray = Array.from(data.names);
      
      // Count how many invoices have each name for this VAT
      const nameCountsForVat = {};
      data.invoiceIds.forEach(invoiceId => {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          const name = (invoice.vendor_name || '').trim().toLowerCase();
          nameCountsForVat[name] = (nameCountsForVat[name] || 0) + 1;
        }
      });
      
      console.log(`📊 Name counts for VAT "${vat}":`, nameCountsForVat);
      
      // Find the name(s) with the minimum count (the minority/typo)
      const minCount = Math.min(...Object.values(nameCountsForVat));
      const minorityNames = Object.keys(nameCountsForVat).filter(name => nameCountsForVat[name] === minCount);
      
      console.log(`⚠️ Minority names (count=${minCount}):`, minorityNames);
      
      // Flag only invoices with the minority name(s)
      data.invoiceIds.forEach(invoiceId => {
        const invoice = invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          const name = (invoice.vendor_name || '').trim().toLowerCase();
          if (minorityNames.includes(name)) {
            console.log(`🎯 Flagging invoice ${invoiceId} with name "${name}"`);
            inconsistentInvoiceIds.add(invoiceId);
          }
        }
      });
    }
  });
  
  // Check for similar vendor names (potential typos)
  // Only check if we have multiple different vendor names
  const vendorNames = Object.keys(nameCount);
  
  if (vendorNames.length > 1) {
    vendorNames.forEach((name1, i) => {
      vendorNames.slice(i + 1).forEach(name2 => {
        const similarity = calculateSimilarity(name1.toLowerCase(), name2.toLowerCase());
        
        // If names are 85% or more similar AND different, check if they should be flagged
        if (similarity >= 0.85 && name1 !== name2) {
          const count1 = nameCount[name1];
          const count2 = nameCount[name2];
          
          // Check if these similar names have the same VAT number
          // If they do, they're already handled by VAT inconsistency check
          const invoicesWithName1 = nameToInvoiceIds[name1].map(id => 
            invoices.find(inv => inv.id === id)
          );
          const invoicesWithName2 = nameToInvoiceIds[name2].map(id => 
            invoices.find(inv => inv.id === id)
          );
          
          const vats1 = new Set(invoicesWithName1.map(inv => (inv.vendor_vat || '').trim()).filter(v => v));
          const vats2 = new Set(invoicesWithName2.map(inv => (inv.vendor_vat || '').trim()).filter(v => v));
          
          // Check if they share any VAT numbers
          const hasCommonVat = [...vats1].some(vat => vats2.has(vat));
          
          // Only flag if NO common VAT AND one has significantly fewer invoices
          if (!hasCommonVat) {
            if (count1 < count2 && count1 <= 3) {
              nameToInvoiceIds[name1].forEach(id => inconsistentInvoiceIds.add(id));
            } else if (count2 < count1 && count2 <= 3) {
              nameToInvoiceIds[name2].forEach(id => inconsistentInvoiceIds.add(id));
            }
          }
        }
      });
    });
  }
  
  return inconsistentInvoiceIds;
};

/**
 * Check if a specific invoice has a unique partner name.
 * @param {Array} invoices - List of invoice objects
 * @param {number} invoiceId - Invoice ID to check
 * @returns {boolean} True if invoice has a unique partner name (when 3+ different names exist)
 */
export const hasUniqueName = (invoices, invoiceId) => {
  const uniqueNames = detectUniquePartnerNames(invoices);
  return uniqueNames.has(invoiceId);
};

/**
 * Validate if invoice contains the company's name or VAT/Tax ID
 * @param {Object} invoice - Invoice object with result data
 * @param {Object} company - Company object with name and vat_number
 * @returns {Object} { isValid: boolean, missing: array of missing items }
 */
export const validateCompanyOnInvoice = (invoice, company, { enabled = null } = {}) => {
  // If explicit enabled flag is passed, use it; otherwise fall back to company flag (backward compat)
  const isEnabled = enabled !== null ? enabled : company?.validate_company_on_scan;
  if (!company || !isEnabled) {
    return { isValid: true, missing: [] };
  }

  const result = invoice.result || {};
  const missing = [];
  
  // Normalize company data for comparison
  const companyName = normalizeCompanyName(company.name);
  const companyVat = (company.vat_number || '').trim().toUpperCase();
  const companyTaxId = companyVat.replace(/^BG/i, ''); // Remove BG prefix for tax ID comparison
  
  // Get all buyer and seller data from invoice
  const buyerName = normalizeCompanyName(result.buyer_name);
  const buyerVat = (result.buyer_vat || '').trim().toUpperCase();
  const buyerTaxId = buyerVat.replace(/^BG/i, '');
  
  const sellerName = normalizeCompanyName(result.seller_name);
  const sellerVat = (result.seller_vat || '').trim().toUpperCase();
  const sellerTaxId = sellerVat.replace(/^BG/i, '');
  
  // Check if company appears as buyer (name OR VAT/Tax ID)
  const buyerNameMatch = companyName && buyerName && 
    (buyerName.includes(companyName) || companyName.includes(buyerName));
  const buyerVatMatch = companyVat && buyerVat && 
    (buyerVat === companyVat || buyerTaxId === companyTaxId);
  const isBuyer = buyerNameMatch || buyerVatMatch;

  // Check if company appears as seller (name OR VAT/Tax ID)
  const sellerNameMatch = companyName && sellerName && 
    (sellerName.includes(companyName) || companyName.includes(sellerName));
  const sellerVatMatch = companyVat && sellerVat && 
    (sellerVat === companyVat || sellerTaxId === companyTaxId);
  const isSeller = sellerNameMatch || sellerVatMatch;

  if (!isBuyer && !isSeller) {
    // Company not found on invoice
    if (companyName) {
      missing.push(`Company name: "${company.name}"`);
    }
    if (companyVat) {
      missing.push(`VAT/Tax ID: "${companyVat}"`);
    }
  }
  
  return {
    isValid: isBuyer || isSeller,
    missing: missing,
    companyName: company.name,
    invoiceNumber: result.invoice_number || 'Unknown'
  };
};

/**
 * Check multiple invoices for company validation
 * @param {Array} invoices - Array of invoice objects
 * @param {Object} company - Company object
 * @returns {Array} Array of invalid invoices with details
 */
export const validateCompanyOnInvoices = (invoices, company, { enabled = null } = {}) => {
  const isEnabled = enabled !== null ? enabled : company?.validate_company_on_scan;
  if (!company || !isEnabled) {
    return [];
  }
  
  const invalidInvoices = [];
  
  invoices.forEach(invoice => {
    const validation = validateCompanyOnInvoice(invoice, company, { enabled: true });
    if (!validation.isValid) {
      invalidInvoices.push({
        id: invoice.id,
        invoiceNumber: validation.invoiceNumber,
        missing: validation.missing,
        companyName: validation.companyName
      });
    }
  });
  
  return invalidInvoices;
};

export default {
  hasMissingInfo,
  getMissingInfoInvoices,
  detectVatConflicts,
  hasVatConflict,
  sortByMissingInfo,
  detectUniquePartnerNames,
  hasUniqueName,
  validateCompanyOnInvoice,
  validateCompanyOnInvoices
};